import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { DataList } from "@/components/common/DataList";
import { PageHeader, Pagination, Spinner, YesNoBadge } from "@/components/common/Common";
import { PersonPicker } from "@/components/common/PersonPicker";
import { RichTextEditor } from "@/components/common/RichTextEditor";
import { useAuth } from "@/context/AuthContext";
import { useList } from "@/hooks/useList";
import { api, fmtDateTime, formatError } from "@/lib/api";
import { DeleteButton, Field } from "./CompanyFormDialog";

const EMPTY = { name: "", abn: "", contact_person_id: null, details_for_email: "", active: true };

function PteDialog({ initial, onClose, onSaved }) {
  const { canWrite, isAdmin } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const isNew = !initial.id;
  const ro = !canWrite;

  useEffect(() => {
    if (isNew) return setForm(EMPTY);
    api.get(`/pte-companies/${initial.id}`).then((r) => setForm(r.data)).catch((e) => { toast.error(formatError(e)); onClose(); });
  }, [initial.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isNew) await api.post("/pte-companies", form);
      else await api.put(`/pte-companies/${initial.id}`, form);
      toast.success("PTE company saved");
      onSaved();
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/pte-companies/${initial.id}`); toast.success("PTE company deleted"); onSaved(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pte-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New PTE company" : form?.name || "PTE company"}</DialogTitle></DialogHeader>
        {!form ? <div className="p-8 flex justify-center"><Spinner /></div> : (
          <form onSubmit={save} className="space-y-4">
            <fieldset disabled={ro} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name *" className="sm:col-span-2"><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pte-name-input" /></Field>
              <Field label="ABN"><Input value={form.abn || ""} onChange={(e) => set("abn", e.target.value)} placeholder="11 digits" data-testid="pte-abn-input" /></Field>
              <Field label="Contact person"><PersonPicker value={form.contact_person_id} onChange={(v) => set("contact_person_id", v)} disabled={ro} testId="pte-contact-picker" /></Field>
              <Field label="Details for email" className="sm:col-span-2">
                <RichTextEditor value={form.details_for_email} onChange={(v) => set("details_for_email", v)} disabled={ro}
                  placeholder="Company details merged into the 'PTE company details' email" testId="pte-details-editor" />
              </Field>
              <label className="flex items-center gap-3 text-sm text-slate-700"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="pte-active-switch" /> Active</label>
            </fieldset>
            {!isNew && <p className="text-xs text-slate-400" data-testid="pte-last-changed">Last changed by {form.updated_by || "—"} · {fmtDateTime(form.updated_at)}</p>}
            <DialogFooter className="gap-2">
              {!isNew && isAdmin && <DeleteButton onConfirm={remove} testId="pte-delete-button" />}
              <Button type="button" variant="outline" onClick={onClose} data-testid="pte-cancel-button">{ro ? "Close" : "Cancel"}</Button>
              {!ro && <Button type="submit" disabled={busy} data-testid="pte-save-button">{busy && <Spinner className="mr-2" />}Save</Button>}
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PteCompanies() {
  const { canWrite } = useAuth();
  const [params, setParams] = useSearchParams();
  const list = useList("/pte-companies");
  const [editing, setEditing] = useState(null);
  useEffect(() => { const id = params.get("edit"); if (id) setEditing({ id }); }, [params]);
  const close = () => { setEditing(null); if (params.get("edit")) setParams({}); };

  const columns = [
    { key: "name", label: "Name", render: (r) => <span className="font-medium text-slate-900">{r.name}</span> },
    { key: "abn", label: "ABN", render: (r) => <span className="font-mono text-xs">{r.abn || "—"}</span> },
    { key: "details", label: "Email details", render: (r) => (r.details_for_email ? "Set" : "Missing") },
    { key: "active", label: "Active", render: (r) => <YesNoBadge value={r.active} /> },
  ];
  return (
    <div>
      <PageHeader title="PTE Companies" subtitle="Contractors' own Pty Ltd employing entities." testId="pte-companies-header"
        actions={canWrite && <Button onClick={() => setEditing({})} data-testid="create-pte-button"><Plus className="h-4 w-4 mr-1" /> New PTE company</Button>} />
      <Input placeholder="Search name or ABN…" value={list.q} onChange={(e) => list.setQ(e.target.value)} className="sm:max-w-sm mb-4 bg-white" data-testid="pte-search-input" />
      <DataList columns={columns} rows={list.items} loading={list.loading} onRowClick={(r) => setEditing(r)} rowTestId="pte-row" />
      <Pagination {...list} testId="pte-pagination" />
      {editing && <PteDialog initial={editing} onClose={close} onSaved={() => { close(); list.reload(); }} />}
    </div>
  );
}
